import React from "react";
import RoomData from "../../../interfaces/robot/rooms/room-data";
import IdTypePoint from "../../../interfaces/robot/rooms/id-type-point";
import ZonePointType from "../../../enums/robot/rooms/zone-point-type";
import { getSvgCoordinatesFromRosPoint } from "../../../interfaces/robot/rooms/point";
import { computeSvgMidPoints } from "../../../utils/map/zone-helper";
import ZoneSvgPoint from "./ZoneSvgPoint";

interface ZonePointHandlesProps {
  /** Ordered zone points (sorting by orderIndex is handled internally). */
  zonePoints: { orderIndex: number; point: IdTypePoint }[];
  roomData: RoomData;
  scale: number;
  /** ZonePointType assigned to the mid-point handles. */
  midPointType: ZonePointType;
  /**
   * Whether the corner handles grow on hover.
   * Typically `true` when the zone is currently selected.
   */
  enlargeCornerHandlesOnHover?: boolean;
  /** When false the mid-point handles are hidden (e.g. while in DeletePoint mode). */
  showMidPoints?: boolean;
  /** Whether mid-point handles can be deleted on click. */
  midPointsDeletable?: boolean;
  /** Set for the room-limit zone, which uses different Redux actions internally. */
  isRoomLimitZonePoint?: boolean;
}

/**
 * Renders the draggable corner handles and insert-mid-point handles for a zone
 * polygon.  Used by both {@link ZonePlannerMapZone} (cleaning/red/rule zones)
 * and {@link RoomLimitZoneSvgPolygon} (limit zone).
 */
const ZonePointHandles: React.FC<ZonePointHandlesProps> = ({
  zonePoints,
  roomData,
  scale,
  midPointType,
  enlargeCornerHandlesOnHover = false,
  showMidPoints = true,
  midPointsDeletable = true,
  isRoomLimitZonePoint = false,
}) => {
  const sortedPoints = [...zonePoints]
    .sort((a, b) => a.orderIndex - b.orderIndex)
    .map((p) => p.point);

  const svgPoints = sortedPoints.map((p) => getSvgCoordinatesFromRosPoint(p, roomData));
  const svgMidPoints = svgPoints.length > 1 ? computeSvgMidPoints(svgPoints) : [];

  return (
    <React.Fragment>
      {/* Corner handles */}
      {sortedPoints.map((p, index) => {
        const svgPoint = svgPoints[index];
        if (svgPoint == null) return null;
        return (
          <ZoneSvgPoint
            key={p.id}
            id={p.id}
            point={svgPoint}
            scale={scale}
            type={p.type}
            enlargingOnHoverEnabled={enlargeCornerHandlesOnHover}
            midPointInsertAtIndex={null}
            isRoomLimitZonePoint={isRoomLimitZonePoint}
          />
        );
      })}

      {/* Mid-point (insert) handles */}
      {showMidPoints &&
        svgMidPoints.map((p, i) => (
          <ZoneSvgPoint
            key={i}
            id={null}
            point={p}
            scale={scale}
            type={midPointType}
            enlargingOnHoverEnabled={true}
            midPointInsertAtIndex={p.insertAtIndex}
            deletable={midPointsDeletable}
            isRoomLimitZonePoint={isRoomLimitZonePoint}
          />
        ))}
    </React.Fragment>
  );
};

export default ZonePointHandles;

